import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/sharp-duotone-solid-svg-icons';
import { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { BookResponse } from 'ts/hotel/interfaces/BookResponse';
import {
  faBed,
  faCalendar,
  faHotel,
  faLocationArrow,
  faLocationDot,
  faMoneyBill,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import Rating from 'components/ui/Rating/Rating';
import { formatPriceCurrency, getCurrency } from 'utils/utils';
import { dateFormat } from 'utils/shared/utils';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { Price } from 'ts/hotel/interfaces/HotelDetails';
import { getKey } from 'utils/hotel';
import { MainContext } from 'contexts/MainContext';
import { defaultLoaderUrl } from 'constants/generic';
import { HotelConfigContext } from 'contexts/HotelConfigContext';

interface BookLoadingModalProps {
  book: BookResponse;
}

const BookLoadingModal: FC<BookLoadingModalProps> = ({ book }) => {
  const [t] = useTranslation();
  const {
    hotelIndex } = useContext(HotelConfigContext);
  const { cdnUrl,
    isEnigma,
    initSiteData
  } = useContext(MainContext);
  const currency = sessionStorage.getItem(getKey('displayCurrencyHotels', hotelIndex)) || 'usd';

  return (
    <div className="modal-overlay modal-overlay-top" onClick={(e) => e.target === e.currentTarget}>
      <div className={'modal modal-sm active'}>
        <div className="title text-center mt-10">{t('hotel.hotel_reservation_in_process')}</div>

        <div className="flex-center flex-column loader-main-content">
          <img
            alt="logo"
            src={(cdnUrl || '') + (isEnigma ? initSiteData?.other?.logoUrl || '' : initSiteData?.other?.mainLoaderUrl || initSiteData?.other?.defaultLoaderUrl || defaultLoaderUrl || '')}
            className="mb-20"
          />
          <FontAwesomeIcon icon={faSpinnerThird as any} spinPulse />
        </div>
        <div className="mt-10">
          <div className="bb-1 pb-10 flex">
            <div className="w200px">
              <FontAwesomeIcon icon={faHotel} className="mr-5" />
              {t('hotel.name')}
            </div>
            <div className="flex-one flex">
              <span className="position-relative" style={{ top: -2 }}>
                <Rating value={book.hotel.item.star} className="mr-5" />
              </span>

              {book.hotel.item.name}
            </div>
          </div>
          <div className="bb-1 pb-10 flex mt-10">
            <div className="w200px">
              <FontAwesomeIcon icon={faLocationArrow} className="mr-5" />
              {t('hotel.address')}
            </div>
            <div className="flex-one flex">
              {book.hotel.addressInfo.city}, {book.hotel.addressInfo.country}{' '}
            </div>
          </div>
          <div className="bb-1 pb-10 flex mt-10">
            <div className="w200px">
              <FontAwesomeIcon icon={faLocationDot} className="mr-5" />
              {t('hotel.location')}
            </div>
            <div className="flex-one flex">{book.hotel.addressInfo.address} </div>
          </div>
          <div className="bb-1 pb-10 flex mt-10">
            <div className="w200px">
              <FontAwesomeIcon icon={faCalendar} className="mr-5" />
              {t('hotel.dates')}
            </div>
            <div className="flex-one flex">
              {dateFormat(book.roomClass.checkIn, 'dd/MM/yyyy')} -{' '}
              {dateFormat(book.roomClass.checkOut, 'dd/MM/yyyy')} [{book.roomClass.nights}{' '}
              {t('hotel.nights')}]
            </div>
          </div>
          <div className="bb-1 pb-10 flex mt-10">
            <div className="w200px">
              <FontAwesomeIcon icon={faBed} className="mr-5" />
              {t('hotel.room')}
            </div>
            <div className="flex-one flex">{book.roomClass.hotelRooms[0].desc}</div>
          </div>
          <div className="bb-1 pb-10 flex mt-10">
            <div className="w200px">
              <FontAwesomeIcon icon={faUsers as any} className="mr-5" />
              {t('hotel.number_of_persons')}
            </div>
            <div className="flex-one flex">{book.paxes.length}</div>
          </div>
          <div className=" pb-10 flex mt-10">
            <div className="w200px">
              <FontAwesomeIcon icon={faMoneyBill} className="mr-5" />
              {t('total')}
            </div>
            <div className="flex-one flex">
              {formatPriceCurrency(
                book.roomClass.price[currency as keyof Price] + book.couponAmount,
                getCurrency(currency)
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(BookLoadingModal, 'BookLoadingModal');
